import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';

import CaretUp from '../assets/caretUp.svg?react';
import CaretUpStroke from '../assets/caretUpStroke.svg?react';

export const Container = styled.button`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 52px;
    padding: 0 16px 0 24px;
    border: none;
    border-left: 4px solid ${(props) => (props.isOpen ? 'var(--blue500)' : 'transparent')};
    border-top: 1px solid var(--gray200);
    border-bottom: 1px solid var(--gray200) ${(props) => props.isOpen && '!important'};
    box-sizing: border-box;
    cursor: pointer;
    outline: none;
    background: transparent;

    &:focus,
    &:focus-visible {
        box-shadow: var(--outline);
    }

    &:focus:not(:focus-visible) {
        box-shadow: none;
    }

    &:hover {
        background-color: rgba(255, 255, 255, 0.7);
        border-left-color: var(--blue500);
    }
`;

export const SectionList = styled.div`
    & > ${Container} {
        border-top: 1px solid var(--gray200);
        border-bottom: none;
    }

    & > ${Container}:last-child {
        border-bottom: 1px solid var(--gray200);
    }
`;

export const IconContainer = styled.div`
    display: flex;
    align-items: center;
    margin-right: 8px;
    fill: var(--gray800);

    & > svg {
        width: 18px;
        height: 18px;
    }
`;

export const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    overflow: hidden;
`;

export const Title = styled.span`
    color: var(--gray800);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.16px;
    line-height: 20px;
    text-transform: uppercase;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
`;

const ExpandableContainer = styled.div`
    transition-duration: 400ms;
    transform: ${(props) => (props.isOpen ? 'rotate(0deg)' : 'rotate(180deg)')};
    transition-delay: 100ms;

    & > svg {
        width: 18px;
        height: 18px;
        fill: var(--blue500);
    }
`;

export const CollaxableContainer = styled.div`
    position: relative;
    box-sizing: border-box;
    padding: 8px 24px 16px 24px;
`;

export const Text = styled.p`
    margin: 0;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.32px;
`;

export const BreakText = styled.span`
    white-space: pre-line;
`;

export const Row = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;

    & > *:not(:last-child) {
        margin-right: 5px;
    }
`;

export const TextBold = styled.span`
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.32px;
`;

export const Link = styled.a`
    display: block;
    text-decoration: none;
    margin: 0;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0.32px;
    font-weight: 600;
    color: var(--blue500);
    outline: none;

    ${(props) =>
        props.disabled &&
        css`
            color: ${(props) => props.theme.colors['surface-grey-700']};
            cursor: initial;
            pointer-events: none;
        `}

    &:focus,
    &:focus-visible {
        box-shadow: var(--outline);
    }

    &:focus:not(:focus-visible) {
        box-shadow: none;
    }
`;

const Section = ({ title, Icon, children, opened = false, onStateChange = () => {} }) => {
    const [isOpen, setIsOpen] = useState(opened);

    const changeState = () => {
        setIsOpen(!isOpen);
        onStateChange(!isOpen);
    };

    return (
        <>
            <Container isOpen={isOpen} onClick={changeState} data-toggle="tooltip" title={title}>
                <TitleContainer>
                    {Icon && (
                        <IconContainer>
                            <Icon />
                        </IconContainer>
                    )}
                    <Title>{title}</Title>
                </TitleContainer>
                <ExpandableContainer isOpen={isOpen}>{isOpen ? <CaretUp /> : <CaretUpStroke />}</ExpandableContainer>
            </Container>
            {isOpen && <CollaxableContainer>{children}</CollaxableContainer>}
        </>
    );
};

Section.propTypes = {
    title: PropTypes.string,
    Icon: PropTypes.node,
    children: PropTypes.node,
    opened: PropTypes.bool,
    onStateChange: PropTypes.func,
};

export default Section;
